<template>
  <div class="directory-node">
    <button
      class="directory-node__row"
      :class="{ 'directory-node__row--child': depth > 0 && !selectable }"
      type="button"
      @click="handleClick"
    >
      <span
        v-if="hasChildren"
        class="directory-node__toggle"
        :class="{ 'directory-node__toggle--open': open }"
      />
      <span v-else-if="selectable" class="directory-node__select" :class="{ active: selected }" />
      <span v-else-if="depth === 0" class="directory-node__indent" />
      <span class="directory-node__label">{{ displayLabel }}</span>
      <span class="directory-node__count">
        <template v-if="doneCount !== null"><b>{{ doneCount }}</b>/{{ count }}</template>
        <template v-else-if="resourceType"><b>{{ count }}</b>{{ resourceLabel }}</template>
        <template v-else>{{ count }}项</template>
      </span>
      <span v-if="!hasChildren" class="directory-node__arrow" />
    </button>
    <div v-if="hasChildren && open" class="directory-node__children">
      <directory-node
        v-for="(child, index) in node.children"
        :key="child.categoryId || child.id || child.questionId || index"
        :node="child"
        :depth="depth + 1"
        :chapter-name="chapterName || (depth === 0 ? displayLabel : '')"
        :selectable="selectable"
        :selected-ids="selectedIds"
        :resource-type="resourceType"
        @leaf="$emit('leaf', $event)"
        @select="$emit('select', $event)"
      />
    </div>
  </div>
</template>

<script>
import { nodeCount, nodeId, nodeLabel, nodeResourceCount } from './shared'

export default {
  name: 'DirectoryNode',
  props: {
    node: {
      type: Object,
      required: true
    },
    depth: {
      type: Number,
      default: 0
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    resourceType: {
      type: String,
      default: ''
    },
    chapterName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      open: Boolean(this.node.expanded)
    }
  },
  computed: {
    hasChildren() {
      return Boolean(this.node.children && this.node.children.length)
    },
    displayLabel() {
      return nodeLabel(this.node)
    },
    count() {
      return this.resourceType
        ? nodeResourceCount(this.node, this.resourceType)
        : nodeCount(this.node)
    },
    doneCount() {
      const value = this.node.studyNum ?? this.node.doneNum ?? this.node.answerNum ?? this.node.completedNum
      return value === undefined || value === null ? null : value
    },
    resourceLabel() {
      return {
        wrong: '项',
        favorite: '项',
        note: '项'
      }[this.resourceType] || ''
    },
    selected() {
      return this.selectedIds.map(String).includes(nodeId(this.node))
    }
  },
  methods: {
    handleClick() {
      if (this.hasChildren) {
        this.open = !this.open
        return
      }
      if (this.selectable) {
        this.$emit('select', this.node)
        return
      }
      this.$emit('leaf', {
        node: this.node,
        chapterName: this.chapterName || (this.depth === 0 ? this.displayLabel : '')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.directory-node__row {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 55px;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  text-align: left;
}

.directory-node__row--child {
  padding-left: 24px;
}

.directory-node__toggle,
.directory-node__select {
  position: relative;
  width: 16px;
  height: 16px;
  margin-right: 9px;
  border-radius: 50%;
  flex: none;
}

.directory-node__toggle {
  background: #168df5;
}

.directory-node__toggle::after {
  position: absolute;
  inset: 0;
  background: url("~@/assets/images/skillCenterQuestion/expand.png") center / 12px 8px no-repeat;
  content: "";
  transition: transform 0.15s ease;
}

.directory-node__toggle--open::after {
  transform: rotate(180deg);
}

.directory-node__select {
  border: 1px solid #c8cbd0;
  background: #ffffff;
}

.directory-node__select.active {
  border-color: transparent;
  background: url("~@/assets/images/skillCenterQuestion/checked.png") center / 16px 16px no-repeat;
}

.directory-node__select.active::after {
  display: none;
}

.directory-node__indent {
  width: 16px;
  margin-right: 9px;
  flex: none;
}

.directory-node__label {
  min-width: 0;
  overflow: hidden;
  font-size: 16px;
  font-weight: 500;
  line-height: 23px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.directory-node__count {
  margin-left: auto;
  color: #9095a5;
  font-size: 14px;
  line-height: 22px;
  white-space: nowrap;
}

.directory-node__count b {
  color: #168df5;
  font-weight: 400;
}

.directory-node__arrow {
  width: 20px;
  height: 20px;
  margin-left: 0;
  background: url("~@/assets/images/skillCenterQuestion/arrow-right.png") center / 12px 12px no-repeat;
}
</style>
